/**
 * UI Update Functions
 * DOM manipulation for all dashboard cards
 */

import { formatTime, formatDateTime, formatLocalTime, degreesToCardinal, getWindDirectionName, windDirectionToDegrees } from './utils.js';
import { formatBreakdownForDisplay } from './rating.js';
import { THRESHOLDS } from './config.js';
import { getDisplayAge, parseISO, now, formatTimestamp, minutesUntil, formatCountdown, formatTimeUntil, breakdownSeconds, parseTimeString } from './time.js';
import { analytics } from './analytics.js';
import { AppState } from './state.js';
import { createHazardAlert, createBreakdownFactor, createElement, clearChildren } from './dom-utils.js';

/**
 * Parse a tide prediction time.
 *
 * Wire format audit (2026-08): /api/get-conditions serves prediction `time`
 * values as ISO 8601 with zone — the ingest fetcher
 * (netlify/functions/ingest/fetchers/noaa.mjs) requests time_zone=gmt from
 * NOAA CO-OPS and JSON-stringifies Date objects into predictions_json
 * ("2026-08-01T14:30:00.000Z"), and the next_high/next_low fallback in
 * get-conditions.mjs buildPredictions() reads timestamptz columns (ISO with
 * offset). A plain ISO parse is therefore correct here.
 *
 * The rare direct-NOAA fallback (fetchConditionsDirectFallback ->
 * /api/predictions, which requests time_zone=lst_ldt) yields naive
 * "YYYY-MM-DD HH:mm" STATION-local strings; parseISO hands those to Date(),
 * which treats them as browser-local — the same behavior as the previous
 * parseNOAATimestamp(time, false) call for that path.
 */
function parseTideTime(time) {
    return parseISO(time);
}

/**
 * Update the ocean status hero card
 */
export function updateOceanStatus(rating) {
    const card = document.getElementById('ocean-status-card');
    const badge = document.getElementById('ocean-rating');
    const ratingText = document.getElementById('rating-text');
    const ratingIcon = document.getElementById('rating-icon');
    const messageEl = document.getElementById('ocean-message');
    const skillEl = document.getElementById('skill-level');
    const alertsEl = document.getElementById('hazard-alerts');

    // Update badge
    badge.className = `status-badge ${rating.rating.toLowerCase()}`;
    ratingText.textContent = rating.rating;
    ratingIcon.textContent = rating.icon; // Now uses Unicode, safe with textContent

    // Update message
    messageEl.textContent = rating.message;

    // Update card styling (uses CSS classes from _utilities.scss instead of inline styles)
    card.classList.remove('rating-good', 'rating-fair', 'rating-poor', 'rating-hazardous');
    card.classList.add(`rating-${rating.rating.toLowerCase()}`);

    // Update details
    const windEl = document.getElementById('detail-wind');
    const swellEl = document.getElementById('detail-swell');
    const tideEl = document.getElementById('detail-tide');
    const tempEl = document.getElementById('detail-water-temp');

    windEl.textContent = rating.details.wind.value;
    windEl.className = `detail-value ${rating.details.wind.rating}`;

    swellEl.textContent = rating.details.swell.value;
    swellEl.className = `detail-value ${rating.details.swell.rating}`;

    tideEl.textContent = rating.details.tide.value;
    tideEl.className = `detail-value ${rating.details.tide.rating}`;

    tempEl.textContent = rating.details.temp.value;
    tempEl.className = `detail-value ${rating.details.temp.rating}`;

    // Update skill recommendation
    skillEl.textContent = `Suitable for: ${rating.skillRecommendation}`;

    // Update hazard alerts (using safe DOM methods)
    if (rating.hazards.length > 0) {
        clearChildren(alertsEl);
        rating.hazards.forEach(h => {
            alertsEl.appendChild(createHazardAlert(h));
        });
        alertsEl.classList.remove('hidden');
    } else {
        alertsEl.classList.add('hidden');
    }

    // Update rating breakdown (Why this rating?)
    updateRatingBreakdown(rating);

    // Track rating calculation in analytics
    analytics.ratingCalculated(
        Math.round(rating.score),
        rating.rating,
        AppState.currentStationId
    );
}

/**
 * Update the "Why this rating?" expandable breakdown section
 */
function updateRatingBreakdown(rating) {
    let breakdownContainer = document.getElementById('rating-breakdown-container');

    // Create the container if it doesn't exist
    if (!breakdownContainer) {
        const oceanStatus = document.querySelector('.ocean-status');
        if (!oceanStatus) return;

        breakdownContainer = document.createElement('div');
        breakdownContainer.id = 'rating-breakdown-container';
        breakdownContainer.className = 'rating-breakdown-container';
        breakdownContainer.innerHTML = `
            <button class="rating-breakdown-toggle" id="rating-breakdown-toggle" aria-expanded="false">
                <span class="toggle-text">Why this rating?</span>
                <span class="toggle-icon">▼</span>
            </button>
            <div class="rating-breakdown-content" id="rating-breakdown-content" hidden>
                <div class="breakdown-header">
                    <span class="breakdown-score">Score: <strong id="breakdown-score-value">--</strong> / 100</span>
                    <a href="/methodology.html" class="breakdown-link" target="_blank">Full methodology →</a>
                </div>
                <div class="breakdown-factors" id="breakdown-factors"></div>
            </div>
        `;

        // Insert after skill recommendation
        const skillEl = document.getElementById('skill-level');
        if (skillEl && skillEl.parentNode) {
            skillEl.parentNode.insertBefore(breakdownContainer, skillEl.nextSibling);
        } else {
            oceanStatus.appendChild(breakdownContainer);
        }

        // Add toggle functionality
        const toggleBtn = document.getElementById('rating-breakdown-toggle');
        const content = document.getElementById('rating-breakdown-content');
        toggleBtn.addEventListener('click', () => {
            const isExpanded = toggleBtn.getAttribute('aria-expanded') === 'true';
            toggleBtn.setAttribute('aria-expanded', !isExpanded);
            content.hidden = isExpanded;
            toggleBtn.querySelector('.toggle-icon').textContent = isExpanded ? '▼' : '▲';
        });
    }

    // Update the breakdown content
    const scoreEl = document.getElementById('breakdown-score-value');
    const factorsEl = document.getElementById('breakdown-factors');

    if (scoreEl && rating.score !== undefined) {
        scoreEl.textContent = Math.round(rating.score);
    }

    if (factorsEl && rating.breakdown) {
        const factors = formatBreakdownForDisplay(rating.breakdown);

        // Use safe DOM methods instead of innerHTML
        clearChildren(factorsEl);
        if (factors.length > 0) {
            factors.forEach(f => {
                factorsEl.appendChild(createBreakdownFactor(f));
            });
        } else {
            const emptyMsg = createElement('p', 'Breakdown data unavailable', { className: 'breakdown-empty' });
            factorsEl.appendChild(emptyMsg);
        }
    }
}

/**
 * Update the swell conditions card
 */
export function updateSwellConditions(buoyData) {
    const heightEl = document.getElementById('swell-height');
    const qualityEl = document.getElementById('swell-quality');
    const periodEl = document.getElementById('swell-period');
    const directionEl = document.getElementById('swell-direction');
    const tempEl = document.getElementById('water-temp');
    const compassArrow = document.getElementById('compass-arrow');
    const swellContent = document.querySelector('#swell-card .swell-content');

    // Remove any existing error state
    const existingError = document.querySelector('#swell-card .error-state');
    if (existingError) existingError.remove();
    if (swellContent) swellContent.classList.remove('hidden');

    if (!buoyData) {
        heightEl.textContent = '—';
        qualityEl.textContent = 'Checking buoy...';
        qualityEl.className = 'swell-quality swell-quality--muted';
        periodEl.textContent = '— sec';
        directionEl.textContent = '—';
        tempEl.textContent = '—';
        showErrorState('swell-card', 'Buoy data temporarily unavailable', 'retryBuoyData');
        if (swellContent) swellContent.classList.add('hidden');
        return;
    }

    if (buoyData.waveHeight !== null && buoyData.waveHeight !== undefined && !isNaN(buoyData.waveHeight)) {
        heightEl.textContent = buoyData.waveHeight.toFixed(1);
    } else {
        heightEl.textContent = '—';
        qualityEl.textContent = 'Height unavailable';
        qualityEl.className = 'swell-quality swell-quality--muted';
    }

    if (buoyData.wavePeriod !== null && buoyData.wavePeriod !== undefined && !isNaN(buoyData.wavePeriod)) {
        periodEl.textContent = `${buoyData.wavePeriod} sec`;

        // Set quality based on period (using THRESHOLDS)
        if (buoyData.wavePeriod >= THRESHOLDS.period.excellent) {
            qualityEl.textContent = 'Clean swell';
            qualityEl.className = 'swell-quality swell-quality--good';
        } else if (buoyData.wavePeriod >= THRESHOLDS.period.good) {
            qualityEl.textContent = 'Good swell';
            qualityEl.className = 'swell-quality swell-quality--good';
        } else if (buoyData.wavePeriod >= THRESHOLDS.period.fair) {
            qualityEl.textContent = 'Fair swell';
            qualityEl.className = 'swell-quality swell-quality--fair';
        } else {
            qualityEl.textContent = 'Choppy';
            qualityEl.className = 'swell-quality swell-quality--poor';
        }
    } else {
        periodEl.textContent = '— sec';
        // Keep quality text if already set, otherwise show generic message
        if (qualityEl.textContent === '--' || qualityEl.textContent === '—') {
            qualityEl.textContent = 'Period unavailable';
            qualityEl.className = 'swell-quality swell-quality--muted';
        }
    }

    if (buoyData.waveDirection !== null && buoyData.waveDirection !== undefined && !isNaN(buoyData.waveDirection)) {
        const cardinal = degreesToCardinal(buoyData.waveDirection);
        directionEl.textContent = `${cardinal} (${Math.round(buoyData.waveDirection)}°)`;
        compassArrow.style.transform = `translate(-50%, -100%) rotate(${buoyData.waveDirection}deg)`;
    } else {
        directionEl.textContent = 'Direction N/A';
        compassArrow.style.transform = 'translate(-50%, -100%) rotate(0deg)';
    }

    if (buoyData.waterTemp !== null && buoyData.waterTemp !== undefined && !isNaN(buoyData.waterTemp)) {
        tempEl.textContent = `${Math.round(buoyData.waterTemp)}°F`;
    } else {
        // Water temp often unavailable from Open-Meteo fallback - show helpful message
        tempEl.textContent = 'N/A';
    }
}

/**
 * Update tide status indicator
 */
export function updateTideStatus(waterLevel, predictions) {
    const indicator = document.getElementById('tide-direction');
    const arrow = document.getElementById('tide-arrow');
    const label = document.getElementById('tide-label');

    indicator.classList.remove('rising', 'falling', 'unavailable');

    if (!predictions || predictions.length < 2) {
        indicator.classList.add('unavailable');
        arrow.textContent = '?';
        label.textContent = 'UNAVAILABLE';
        return;
    }

    const currentTime = now();
    const nextTide = predictions.find(p => parseTideTime(p.time) > currentTime);

    if (nextTide) {
        const isRising = nextTide.type === 'H';
        indicator.classList.add(isRising ? 'rising' : 'falling');
        arrow.textContent = isRising ? '\u2191' : '\u2193'; // Unicode arrows instead of HTML entities
        label.textContent = isRising ? 'RISING' : 'FALLING';
    } else {
        indicator.classList.add('unavailable');
        arrow.textContent = '?';
        label.textContent = 'UNAVAILABLE';
    }
}

/**
 * Helper to safely format a number
 * Returns contextual placeholder instead of "--" when data unavailable
 */
function safeFixed(value, digits = 1, context = 'value') {
    if (value === null || value === undefined || isNaN(value)) {
        // Return contextual placeholders instead of "--"
        switch (context) {
            case 'height':
                return '—'; // Em dash looks cleaner
            case 'temp':
                return '—';
            case 'level':
                return '—';
            default:
                return '—';
        }
    }
    return value.toFixed(digits);
}

/**
 * Update current water level display
 */
export function updateWaterLevel(data) {
    const levelEl = document.getElementById('current-level');
    const timeEl = document.getElementById('level-time');

    if (!data || data.value === null || data.value === undefined || isNaN(data.value)) {
        levelEl.textContent = '—';
        levelEl.classList.add('unavailable');
        // Check if we have predictions - if so, this is likely a prediction-only station
        timeEl.textContent = 'Estimated from tide predictions';
        return;
    }

    levelEl.textContent = safeFixed(data.value, 2);
    levelEl.classList.remove('unavailable');
    // Time may not be available from Supabase cached data
    if (data.time) {
        timeEl.textContent = `as of ${formatTime(data.time)}`;
    } else {
        timeEl.textContent = 'Current reading';
    }
}

/**
 * Update next tides display
 */
export function updateNextTides(predictions) {
    const highValueEl = document.getElementById('next-high-value');
    const highTimeEl = document.getElementById('next-high-time');
    const lowValueEl = document.getElementById('next-low-value');
    const lowTimeEl = document.getElementById('next-low-time');

    if (!predictions || predictions.length === 0) {
        highValueEl.textContent = '—';
        highTimeEl.textContent = 'Loading...';
        lowValueEl.textContent = '—';
        lowTimeEl.textContent = 'Loading...';
        return;
    }

    const currentTime = now();
    const futurePredictions = predictions.filter(p => parseTideTime(p.time) > currentTime);

    const nextHigh = futurePredictions.find(p => p.type === 'H');
    const nextLow = futurePredictions.find(p => p.type === 'L');

    if (nextHigh && nextHigh.value !== null && !isNaN(nextHigh.value)) {
        highValueEl.textContent = safeFixed(nextHigh.value, 1) + ' ft';
        highTimeEl.textContent = formatDateTime(nextHigh.time);
    } else {
        highValueEl.textContent = '—';
        highTimeEl.textContent = 'Checking...';
    }

    if (nextLow && nextLow.value !== null && !isNaN(nextLow.value)) {
        lowValueEl.textContent = safeFixed(nextLow.value, 1) + ' ft';
        lowTimeEl.textContent = formatDateTime(nextLow.time);
    } else {
        lowValueEl.textContent = '—';
        lowTimeEl.textContent = 'Checking...';
    }
}

/**
 * Update weather display
 */
export function updateWeather(data) {
    const speedEl = document.getElementById('wind-speed');
    const directionTextEl = document.getElementById('wind-direction-text');
    const arrowEl = document.getElementById('wind-arrow');
    const summaryEl = document.getElementById('weather-summary');
    const tempEl = document.getElementById('temperature');
    const weatherContent = document.querySelector('#weather-card .weather-content');

    // Remove any existing error state
    const existingError = document.querySelector('#weather-card .error-state');
    if (existingError) existingError.remove();
    if (weatherContent) weatherContent.classList.remove('hidden');

    if (!data) {
        speedEl.textContent = '— mph';
        directionTextEl.textContent = '';
        summaryEl.textContent = 'Checking weather...';
        tempEl.textContent = '—';
        arrowEl.style.transform = 'rotate(0deg)';
        showErrorState('weather-card', 'Weather data unavailable', 'retryWeatherData');
        if (weatherContent) weatherContent.classList.add('hidden');
        return;
    }

    speedEl.textContent = data.windSpeed || '— mph';

    if (data.windDirection) {
        directionTextEl.textContent = `from ${getWindDirectionName(data.windDirection)}`;
        const degrees = windDirectionToDegrees(data.windDirection);
        arrowEl.style.transform = `rotate(${degrees + 180}deg)`;
    } else {
        directionTextEl.textContent = 'Variable';
    }

    if (data.temperature !== undefined && data.temperature !== null) {
        // Pipeline temperatures are always Fahrenheit; the API sends no unit field
        tempEl.textContent = `${data.temperature}°${data.temperatureUnit || 'F'}`;
    } else {
        tempEl.textContent = '—';
    }

    summaryEl.textContent = data.shortForecast || 'No forecast available';
}

/**
 * Update sun times display
 */
export function updateSunTimes(sunData) {
    const sunriseEl = document.getElementById('sunrise-time');
    const sunsetEl = document.getElementById('sunset-time');
    const dayLengthEl = document.getElementById('day-length');

    if (!sunData) {
        sunriseEl.textContent = '—';
        sunsetEl.textContent = '—';
        dayLengthEl.textContent = 'Calculating...';
        return;
    }

    // Format times in local timezone using the utility
    sunriseEl.textContent = formatLocalTime(sunData.sunrise, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });
    sunsetEl.textContent = formatLocalTime(sunData.sunset, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });

    // Calculate day length from seconds or parse if string
    let dayLengthSecs;
    if (typeof sunData.dayLength === 'number') {
        dayLengthSecs = sunData.dayLength;
    } else if (typeof sunData.dayLength === 'string') {
        dayLengthSecs = parseTimeString(sunData.dayLength);
        if (dayLengthSecs === null) {
            dayLengthEl.textContent = '';
            return;
        }
    } else {
        dayLengthEl.textContent = '';
        return;
    }

    const { hours, minutes } = breakdownSeconds(dayLengthSecs);
    dayLengthEl.textContent = `${hours}h ${minutes}m of daylight`;
}

/**
 * Update tide hero section in the main status card
 */
export function updateTideHeroSection(waterLevel, predictions) {
    const directionEl = document.getElementById('tide-hero-direction');
    const arrowEl = document.getElementById('tide-hero-arrow');
    const stateEl = document.getElementById('tide-hero-state');
    const levelEl = document.getElementById('tide-hero-level');
    const countdownLabelEl = document.getElementById('countdown-label');
    const countdownTimeEl = document.getElementById('countdown-time');
    const countdownDetailEl = document.getElementById('countdown-detail');
    const countdownContainer = document.getElementById('tide-countdown');

    directionEl.classList.remove('rising', 'falling');

    if (!predictions || predictions.length === 0) {
        stateEl.textContent = 'UNKNOWN';
        arrowEl.textContent = '?';
        levelEl.textContent = '-- ft';
        countdownTimeEl.textContent = '--:--';
        countdownDetailEl.textContent = 'Data unavailable';
        return;
    }

    const currentTime = now();
    const futurePredictions = predictions.filter(p => parseTideTime(p.time) > currentTime);
    const nextTide = futurePredictions[0];

    if (nextTide) {
        const isRising = nextTide.type === 'H';
        directionEl.classList.add(isRising ? 'rising' : 'falling');
        arrowEl.textContent = isRising ? '\u2191' : '\u2193'; // Unicode arrows
        stateEl.textContent = isRising ? 'RISING' : 'FALLING';

        // Update countdown
        countdownContainer.classList.remove('high', 'low');
        countdownContainer.classList.add(nextTide.type === 'H' ? 'high' : 'low');
        countdownLabelEl.textContent = nextTide.type === 'H' ? 'Next High Tide' : 'Next Low Tide';

        const tideTime = parseTideTime(nextTide.time);
        const totalMins = minutesUntil(tideTime);
        countdownTimeEl.textContent = formatCountdown(totalMins, { verbose: totalMins < 60 });

        countdownDetailEl.textContent = `${safeFixed(nextTide.value, 1)} ft at ${formatTime(nextTide.time)}`;
    } else {
        stateEl.textContent = 'UNKNOWN';
        arrowEl.textContent = '?';
        countdownTimeEl.textContent = '\u2014';
        countdownDetailEl.textContent = 'Checking tide data...';
    }

    if (waterLevel && waterLevel.value !== null && !isNaN(waterLevel.value)) {
        levelEl.textContent = `${safeFixed(waterLevel.value, 2)} ft`;
    } else {
        levelEl.textContent = '— ft';
    }
}

/**
 * Update beach window indicator
 */
export function updateBeachWindow(waterLevel, predictions) {
    const windowEl = document.getElementById('beach-window');
    const statusEl = document.getElementById('beach-status');
    const detailEl = document.getElementById('beach-detail');

    windowEl.classList.remove('active', 'upcoming', 'inactive');

    if (!predictions || predictions.length === 0 || !waterLevel) {
        statusEl.textContent = '—';
        detailEl.textContent = 'Checking conditions...';
        windowEl.classList.add('inactive');
        return;
    }

    const currentLevel = waterLevel.value;
    const currentTime = now();

    // Find next low and high tides
    const futurePredictions = predictions.filter(p => parseTideTime(p.time) > currentTime);
    const nextLow = futurePredictions.find(p => p.type === 'L');
    const nextTide = futurePredictions[0];

    // Determine if currently in beach window
    const isMidTide = currentLevel >= THRESHOLDS.tide.low && currentLevel <= THRESHOLDS.tide.midHigh;
    const isFallingTowardLow = nextTide && nextTide.type === 'L';

    let isNearLowTide = false;
    let minutesToLow = null;
    if (nextLow) {
        minutesToLow = minutesUntil(parseTideTime(nextLow.time));
        isNearLowTide = minutesToLow <= THRESHOLDS.beach.nearLowTide;
    }

    // Calculate beach window status (tide-based beach access)
    if ((isMidTide && isFallingTowardLow) || isNearLowTide) {
        windowEl.classList.add('active');
        statusEl.textContent = 'GOOD';

        if (isNearLowTide && minutesToLow <= 60) {
            detailEl.textContent = 'Near low tide - more beach exposed';
        } else if (isFallingTowardLow) {
            detailEl.textContent = 'Tide falling - beach expanding';
        } else {
            detailEl.textContent = 'Mid-tide - decent beach access';
        }
    } else if (nextLow && minutesToLow <= THRESHOLDS.beach.upcomingWindow) {
        windowEl.classList.add('upcoming');
        statusEl.textContent = formatTimeUntil(minutesToLow);
        detailEl.textContent = `More beach at ${formatTime(nextLow.time)}`;
    } else if (currentLevel > THRESHOLDS.tide.high) {
        windowEl.classList.add('inactive');
        statusEl.textContent = 'Limited';
        detailEl.textContent = 'High tide - less beach area';
    } else if (nextLow) {
        windowEl.classList.add('upcoming');
        statusEl.textContent = formatTimeUntil(minutesToLow, { showOnlyHours: true });
        detailEl.textContent = `Best around ${formatTime(nextLow.time)}`;
    } else {
        windowEl.classList.add('inactive');
        statusEl.textContent = 'Check Later';
        detailEl.textContent = 'Tide data pending';
    }
}

/**
 * Update timestamp in footer using client's local timezone
 */
export function updateTimestamp() {
    const el = document.getElementById('last-update');
    el.textContent = `Last updated: ${formatTimestamp()}`;
}

/**
 * Update freshness indicators from Supabase conditions response
 * Uses the single updated_at timestamp from the conditions endpoint
 */
export function updateFreshnessFromConditions(updatedAt) {
    if (!updatedAt) return;

    const { text, class: className } = getDisplayAge(updatedAt);

    // Update all freshness indicators with same value (data is from single source)
    ['swell-freshness', 'weather-freshness', 'tide-freshness', 'sun-freshness'].forEach(id => {
        const el = document.getElementById(id);
        if (el) {
            el.textContent = text;
            el.className = `data-freshness ${className}`;
        }
    });
}

/**
 * Show error state with retry button
 * Uses safe DOM methods instead of innerHTML to prevent XSS
 */
export function showErrorState(cardId, message, retryFnName) {
    const card = document.getElementById(cardId);
    if (!card) return;

    let errorEl = card.querySelector('.error-state');
    if (!errorEl) {
        errorEl = document.createElement('div');
        errorEl.className = 'error-state';
        const header = card.querySelector('.card-header') || card.querySelector('h2');
        if (header && header.nextElementSibling) {
            header.nextElementSibling.classList.add('hidden');
        }
        card.appendChild(errorEl);
    }

    // Use safe DOM methods instead of innerHTML
    clearChildren(errorEl);

    const iconSpan = createElement('span', '\u26A0\uFE0F', { className: 'error-icon' });
    const messageSpan = createElement('span', message, { className: 'error-message' });

    errorEl.appendChild(iconSpan);
    errorEl.appendChild(messageSpan);

    if (retryFnName && typeof window[retryFnName] === 'function') {
        const retryBtn = createElement('button', 'Retry', { className: 'retry-btn' });
        retryBtn.addEventListener('click', () => window[retryFnName]());
        errorEl.appendChild(retryBtn);
    }
}
